import type { AxiosInstance } from 'axios';

/**
 * Whether a customer may review a product.
 *
 * `orderLineItemId` is the purchase the review would be attached to — one
 * review per purchase, so a customer who bought twice may review twice.
 */
export interface ReviewEligibility {
  canReview: boolean;
  alreadyReviewed: boolean;
  orderLineItemId?: string;
  reason?: 'NOT_PURCHASED' | 'ALREADY_REVIEWED';
}

export interface Review {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  status: string;
  customer: { id: string; name: string };
  createdAt: string;
}

export interface ReviewListResponse {
  data: Review[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

export function reviewEndpoints(http: AxiosInstance) {
  return {
    // public
    listProductReviews: (productId: string, params?: { page?: number; pageSize?: number }) =>
      http
        .get<ReviewListResponse>(`/products/${productId}/reviews`, { params })
        .then((r) => r.data),

    getProductRating: (productId: string) =>
      http
        .get<{
          avgRatingX100: number;
          reviewCount: number;
          ratingDistribution: Record<number, number>;
        }>(`/products/${productId}/rating`)
        .then((r) => r.data),

    // customer
    /**
     * Whether the signed-in customer may review this product, and against
     * which purchase. Lets a product page choose between offering the form,
     * saying it has already been reviewed, and saying nothing — instead of
     * offering the form to everyone and failing the write.
     */
    checkReviewEligibility: (productId: string) =>
      http
        .get<{ data: ReviewEligibility }>(`/products/${productId}/review-eligibility`)
        .then((r) => r.data.data),

    createReview: (body: { productId: string; rating: number; title?: string; body?: string }) =>
      http.post<Review>('/reviews', body).then((r) => r.data),
  };
}

export type ReviewEndpoints = ReturnType<typeof reviewEndpoints>;
